import React, { useState, useCallback, memo, useRef, useMemo } from "react";
import { Table, Input } from "antd";
import "./TableDetailDescription.css";
import { UpdateMonthDetailDescription } from "../../api/APIDescriptionDetail";
import { EditOutlined, FileTextOutlined } from "@ant-design/icons";
import { getLogsDescriptionDay } from "../../api/APIDescriptionDetail";
import ModalLogDescriptionDay from "./ModalLogDescriptionDay";

const currentMonth = new Date().getMonth() + 1;
const currentDate = new Date().getDate();

// Tách cell thành component riêng để tránh re-render không cần thiết
const EditableCell = memo(({
  record,
  date,
  dayData,
  isEditing,
  isNull,
  editValue,
  onEdit,
  onLogs,
  onChange,
  onKeyDown,
  onBlur,
  inputRef,
  valueMonth,
  isShowBtnEdit,
  isRowClosed
}) => {
  if (!dayData) return "-";

  // Bôi gạch chân đỏ dưới Input nếu giá trị log input api sửa nhiều hơn 1
  const inputStyle = {
    marginBottom: 5,
    ...(dayData.log > 1 && {
      borderBottom: '3px solid red',
      boxShadow: 'none'
    })
  };

  // Add style for display value when log count > 1
  const displayValueStyle = {
    marginBottom: 5,
    ...(dayData.log > 1 && {
      borderBottom: '3px solid red',
    })
  };

  const shouldShowEditButton = isShowBtnEdit || (
    (valueMonth === currentMonth && (
      // Nếu ngày hiện tại <= 23, hiển thị tất cả các ngày
      currentDate <= 23 ||
      // Nếu ngày hiện tại > 23, chỉ hiển thị các ngày > 20
      (currentDate > 23 && date > 20)
    ))
  );

  // Xác định liệu cell có nên được hiển thị với background màu xám hay không
  const isGrayBackground = !shouldShowEditButton;

  return (
    <div style={{ textAlign: "right" }}
      // className={!shouldShowBtnEdit ? 'input-lock-gray-background' : ''}
      className={isGrayBackground ? 'input-lock-gray-background' : ''}
    >
      {isNull || isEditing ? (
        <Input
          ref={inputRef}
          type="number"
          style={inputStyle}
          value={editValue}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          disabled={!shouldShowEditButton || isRowClosed}
        />
      ) : (
        <>
          {shouldShowEditButton ? (
            <>
              <EditOutlined
                title="Chỉnh sửa"
                style={{ cursor: "pointer", color: "#1890ff", marginRight: 5, fontSize: '1.5rem' }}
                onClick={() => onEdit(record, date)}
              />
              <FileTextOutlined
                title="Logs"
                style={{ cursor: "pointer", color: "#52c41a", marginRight: 5, fontSize: '1.5rem' }}
                onClick={() => onLogs(record, date)}
              />
            </>
          ) : (
            // Luôn hiển thị icon Logs, ngay cả khi ẩn icon Edit
            <FileTextOutlined
              title="Logs"
              style={{ cursor: "pointer", color: "#52c41a", marginRight: 5, fontSize: '1.5rem' }}
              onClick={() => onLogs(record, date)}
            />
          )}
          <div style={displayValueStyle}>{dayData.quantity}</div>
        </>
      )}
    </div>
  );
});

const TableDetailDescription = ({ data: initialData, showAllColumns, idProjectDescription, valueYear, valueMonth, updateTotalMoney, isShowBtnEdit }) => {
  const [editingState, setEditingState] = useState({ key: null, date: null });
  const [editedValues, setEditedValues] = useState({});
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [logsDayData, setLogsDayData] = useState(null);
  const [valueDay, setValueDay] = useState(null);
  // Update 6 cột dữ liệu
  const [data, setData] = useState(initialData);

  // Update 6 cột Data
  // const fetchUpdateSixColumns = useCallback(async () => {
  //   try {
  //     const params = {
  //       id_project: idProjectDescription,
  //       year: valueYear,
  //       month: valueMonth,
  //     }
  //     const res = await GetDataDetailDescription(params);
  //     if (res.data) {
  //       setData(res?.data?.listDescription);
  //       // Cập nhật tổng tiền sau khi cập nhật dữ liệu
  //       updateTotalMoney(res?.data?.sum_project);
  //     }
  //   } catch (error) {
  //     console.log('Fail update data:', error);
  //   }
  // }, [data])

  // Quản lý focus select cho input
  const inputRef = useRef(null)

  // Kiểm soát việc blur Input
  const shouldBlurRef = useRef(true);

  // Hàm lấy ra tất cả các ngày thường và các giá trị quantity, amount riêng
  const allDates = useMemo(() => {
    if (!data?.length) return [];
    const regularDates = data[0].list_day
      .filter(day => !isNaN(parseInt(day.date)))
      .map(day => parseInt(day.date))
      .sort((a, b) => a - b);
    return regularDates;
  }, [data]);

  // Hàm lấy giá trị quantity, amount từ value from list_day
  const getSummaryValue = (record, summaryType) => {
    const summaryDay = record.list_day.find(day => day.date === summaryType);
    return summaryDay?.quantity ?? 0;
  };

  /// Chuyển đến ô Input kế tiếp trong cùng cột
  const moveToNextColumnInput = useCallback((currentRecord, currentDate) => {
    const tableData = data.map((item, index) => ({
      key: index,
      ...item
    }));

    const currentIndex = tableData.findIndex(record => record.key === currentRecord.key);
    // Xử lý di chuyển trong cùng cột
    if (currentIndex < tableData.length - 1) {
      const nextRecord = tableData[currentIndex + 1];
      const nextDayData = nextRecord.list_day.find(day => day.date === currentDate);
      if (nextDayData) {
        // Đặt timeout nhỏ để đảm bảo state đã được cập nhật
        setTimeout(() => {
          handleEdit(nextRecord, currentDate);
          // Focus vào input mới sau khi nó được render
          if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
          }
        }, 30);
      }
    } else {
      // Kiểm tra và di chuyển sang cột tiếp theo nếu đã ở cuối cùng
      const currentDateIndex = allDates.findIndex(date => date === currentDate);
      if (currentDateIndex < allDates.length - 1) {
        const nextDate = allDates[currentDateIndex + 1];
        const firstRecord = tableData[0];
        const nextDayData = firstRecord.list_day.find(day => day.date === nextDate);
        if (nextDayData) {
          // Đặt timeout nhỏ để đảm bảo state đã được cập nhật
          setTimeout(() => {
            handleEdit(firstRecord, nextDate);
            // Focus vào input mới sau khi nó được render
            if (inputRef.current) {
              inputRef.current.focus();
              inputRef.current.select();
            }
          }, 30);
        } else {
          setTimeout(() => {
            setEditingState({ key: null, date: null });
          }, 30);
        }
      } else {
        setTimeout(() => {
          setEditingState({ key: null, date: null });
        }, 30);
      }

    }
  }, [data, allDates]);

  // Memoize handlers để tránh tạo mới function mỗi lần render
  const handleEdit = useCallback((record, date) => {
    const dayData = record.list_day.find((day) => day.date === date);
    if (dayData) {
      setEditingState({ key: record.key, date });
      // Chỉ set giá trị mới nếu chưa có trong editedValues
      if (!editedValues[`${record.key}-${date}`]) {
        setEditedValues(prev => ({
          ...prev,
          [`${record.key}-${date}`]: dayData.quantity?.toString() ?? ""
        }));
      }
      // Focus vào input sau khi set state
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 30);
    }
  }, [editedValues]);

  const handleLogs = useCallback((record, date) => {
    const dayData = record.list_day.find((day) => day.date === date);
    if (dayData) {
      setValueDay(dayData.date);
      getLogsDescriptionDay({ id: dayData.detail_id })
        .then((re) => {
          setLogsDayData(re.data.log);
          setLogsModalOpen(true);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, []);

  const handleChangeInput = useCallback((e, record, date) => {
    const newValue = e.target.value;
    setEditedValues(prev => ({
      ...prev,
      [`${record.key}-${date}`]: newValue
    }));
  }, []);

  // Function to calculate summary values for a record
  const calculateSummaryValues = useCallback((record) => {
    const list_day = record.list_day;

    // Initialize summary values
    let quantity_1_20 = 0;
    let amount_1_20 = 0;
    let quantity_20_end = 0;
    let amount_20_end = 0;

    // Calculate quantities and amounts for days 1-20 and 21-end
    list_day.forEach(day => {
      if (!isNaN(parseInt(day.date))) {
        const dayNumber = parseInt(day.date);
        const quantity = parseFloat(day.quantity) || 0;
        const unitPrice = parseFloat(record?.unit_price) || 0;
        // const unitPrice = 1;
        const amount = quantity * unitPrice;

        if (dayNumber <= 20) {
          quantity_1_20 += quantity;
          amount_1_20 += amount;
        } else {
          quantity_20_end += quantity;
          amount_20_end += amount;
        }
      }
    });

    // Calculate totals
    const total_quantity = quantity_1_20 + quantity_20_end;
    const sum_amount = amount_1_20 + amount_20_end;

    // Update the summary days in list_day
    const updatedListDay = list_day.map(day => {
      switch (day.date) {
        case 'Quantity_1_20':
          return { ...day, quantity: quantity_1_20 };
        case 'Amount_1_20':
          return { ...day, quantity: amount_1_20 };
        case 'Quantity_20_end':
          return { ...day, quantity: quantity_20_end };
        case 'Amount_20_end':
          return { ...day, quantity: amount_20_end };
        case 'Total_Quantity':
          return { ...day, quantity: total_quantity };
        case 'Sum_amount':
          return { ...day, quantity: sum_amount };
        default:
          return day;
      }
    });

    return { ...record, list_day: updatedListDay };
  }, []);


  // Call API để cập nhật Input, tính tổng 6 cột, Move đến Input tiếp
  const handleSaveInput = useCallback((record, date, quantity) => {
    const dayData = record.list_day.find((day) => day.date === date);
    if (dayData) {
      var FormData = require("form-data");
      var dataSave = new FormData();
      dataSave.append("id", dayData.detail_id);
      dataSave.append("quantity", quantity);

      UpdateMonthDetailDescription(dataSave)
        .then((res) => {
          if (res.data.message === "ok") {
            // Update data locally
            setData(prevData => {
              const newData = prevData.map(item => {
                if (item.description_name === record.description_name) {
                  // Update the specific day's quantity
                  const updatedListDay = item.list_day.map(day => {
                    if (day.date === date) {
                      return { ...day, quantity: parseFloat(quantity) };
                    }
                    return day;
                  });

                  // Calculate new summary values
                  const updatedRecord = { ...item, list_day: updatedListDay };
                  return calculateSummaryValues(updatedRecord);
                }
                return item;
              });

              // Calculate and update total money
              const totalMoney = newData.reduce((sum, item) => {
                const sumAmountDay = item.list_day.find(day => day.date === 'Sum_amount');
                return sum + (parseFloat(sumAmountDay?.quantity) || 0);
              }, 0);

              updateTotalMoney(totalMoney);

              return newData;
            });

            dayData.quantity = quantity; // Cập nhật giá trị mới
            // Xóa giá trị đã edit sau khi lưu thành công
            setEditedValues(prev => {
              const newValues = { ...prev };
              delete newValues[`${record.key}-${date}`];
              return newValues;
            });

            // Fetch lại dữ liệu 6 cột sau khi lưu thành công
            // await fetchUpdateSixColumns();

            console.log("Cập nhật thành công");
            // Chuyển đến ô Input kế tiếp sau khi lưu thành công
            moveToNextColumnInput(record, date);

          } else {
            console.log("Cập nhật thất bại");
          }
        })
        .catch(() => {
          console.log("Cập nhật thất bại");
        });
    }
  }, [calculateSummaryValues, moveToNextColumnInput, updateTotalMoney]);


  // Hàm di chuyển theo chiều dọc (Lên hoặc Xuống) giữa các Input
  const moveToVerticalInput = useCallback((currentRecord, currentDate, direction) => {
    const tableData = data.map((item, index) => ({
      key: index,
      ...item
    }));

    const currentIndex = tableData.findIndex(record => record.key === currentRecord.key);
    const nextIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (nextIndex >= 0 && nextIndex < tableData?.length) {
      const nextRecord = tableData[nextIndex];
      const nextDayData = nextRecord.list_day.find(day => day.date === currentDate);
      if (nextDayData) {
        // Tạm thời disable blur trước khi chuyển Input
        shouldBlurRef.current = false;

        // Set trạng thái editing cho ô mới
        setEditingState({ key: nextRecord.key, date: currentDate });

        // Set giá trị cho ô mới nếu chưa có trong editedValues
        if (!editedValues[`${nextRecord.key}-${currentDate}`]) {
          setEditedValues(prev => ({
            ...prev,
            [`${nextRecord.key}-${currentDate}`]: nextDayData?.quantity?.toString() ?? ""
          }));
        }

        // Focus vào input mới
        setTimeout(() => {
          // handleEdit(nextRecord, currentDate);
          if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
          }

          // Bật lại blur sau khi đã focus xong
          shouldBlurRef.current = true;
        }, 30);
      }
    }
  }, [data, editedValues])

  // Hàm di chuyển theo chiều ngang (Trái hoặc phải) giữa các Input
  const moveToHorizontalInput = useCallback((currentRecord, currentDate, direction) => {
    const currentDateIndex = allDates.findIndex(date => date === currentDate);
    const nextDateIndex = direction === 'left' ? currentDateIndex - 1 : currentDateIndex + 1;
    if (nextDateIndex >= 0 && nextDateIndex < allDates.length) {
      const nextDate = allDates[nextDateIndex];
      const dayData = currentRecord.list_day.find(day => day.date === nextDate);
      if (dayData) {
        shouldBlurRef.current = false;

        setEditingState({ key: currentRecord.key, date: nextDate });

        if (!editedValues[`${currentRecord.key}-${nextDate}`]) {
          setEditedValues(prev => ({
            ...prev,
            [`${currentRecord.key}-${nextDate}`]: dayData?.quantity?.toString() ?? ""
          }));
        }

        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select(1);
          }
          shouldBlurRef.current = true;
        }, 30)
      }
    }
  }, [allDates, editedValues])

  // Hàm xử lý Enter, F8, F9 để Save Input
  // Phím điều hướng mũi tên lên/xuống để di chuyển lên/xuống
  const handleKeyDown = useCallback((e, record, date) => {
    if (e.key === "Enter" || e.key === "F8" || e.key === "F9") {
      e.preventDefault();
      const newValue = editedValues[`${record.key}-${date}`];
      handleSaveInput(record, date, newValue);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      moveToVerticalInput(record, date, 'up');
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      moveToVerticalInput(record, date, 'down');
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      moveToHorizontalInput(record, date, 'left');
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      moveToHorizontalInput(record, date, 'right')
    }
  }, [editedValues, handleSaveInput, moveToVerticalInput, moveToHorizontalInput]);


  // Chỉ Blur khi được cho phép
  const handleBlur = useCallback((record, date) => {
    if (shouldBlurRef.current) {
      // Chỉ tắt chế độ edit khi click ra ngoài, không tự động lưu
      setTimeout(() => {
        setEditingState({ key: null, date: null });
      }, 50)
    }
  }, []);

  // Định nghĩa base column
  const baseColumns = [
    {
      title: "No.",
      key: "index",
      fixed: "left",
      width: 40,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Description Name",
      dataIndex: "description_name",
      key: "description_name",
      fixed: "left", // Cố định cột đầu tiên
      width: 170,
    },
    {
      title: "Pricing Models",
      dataIndex: "pricing_models",
      key: "pricing_models",
      width: 100,
      hidden: !showAllColumns,
    },
    {
      title: "Project State",
      dataIndex: "project_stage",
      key: "project_stage",
      width: 100,
      hidden: !showAllColumns,
    },
    {
      title: "Project Description",
      dataIndex: "project_description",
      key: "project_description",
      width: 150,
      hidden: !showAllColumns,
    },
    {
      title: "Sub Project Name 1",
      dataIndex: "sub_project_name_1",
      key: "sub_project_name_1",
      width: 120,
      hidden: !showAllColumns,
    },
    {
      title: "Sub Project Name 2",
      dataIndex: "sub_project_name_2",
      key: "sub_project_name_2",
      width: 120,
      hidden: !showAllColumns,
    },
    {
      title: "Quantity 1-20",
      key: "quantity_1_20",
      width: 110,
      hidden: !showAllColumns,
      render: (_, record) => (
        <div style={{ textAlign: "right" }}>
          {getSummaryValue(record, "Quantity_1_20").toLocaleString()}
        </div>
      )
    },
    {
      title: "Amount 1-20",
      key: "amount_1_20",
      width: 110,
      hidden: !showAllColumns,
      render: (_, record) => (
        <div style={{ textAlign: "right" }}>
          {getSummaryValue(record, "Amount_1_20").toLocaleString()}
        </div>
      )
    },
    {
      title: "Quantity 20-end",
      key: "quantity_20_end",
      width: 130,
      hidden: !showAllColumns,
      render: (_, record) => (
        <div style={{ textAlign: "right" }}>
          {getSummaryValue(record, "Quantity_20_end").toLocaleString()}
        </div>
      )
    },
    {
      title: "Amount 20-end",
      key: "amount_20_end",
      width: 120,
      hidden: !showAllColumns,
      render: (_, record) => (
        <div style={{ textAlign: "right" }}>
          {getSummaryValue(record, "Amount_20_end").toLocaleString()}
        </div>
      )
    },
    {
      title: "Total Quantity",
      key: "total_quantity",
      width: 110,
      hidden: !showAllColumns,
      render: (_, record) => (
        <div style={{ textAlign: "right" }}>
          {getSummaryValue(record, "Total_Quantity").toLocaleString()}
        </div>
      )
    },
    {
      title: "Sum Amount",
      key: "sum_amount",
      width: 110,
      hidden: !showAllColumns,
      render: (_, record) => (
        <div style={{ textAlign: "right" }}>
          {getSummaryValue(record, "Sum_amount").toLocaleString()}
        </div>
      )
    }
  ];

  const tableColumns = useMemo(() => {
    // Lọc ra các cột ẩn
    const visibleBaseColumns = baseColumns.filter(col => !col.hidden);

    // Thêm date columns (chỉ cho các ngày 1-31)
    const dateColumns = allDates.map(date => ({
      title: `${date}`,
      dataIndex: `date_${date}`,
      key: `date_${date}`,
      width: 150,
      render: (_, record) => {
        const dayData = record.list_day.find(day => day.date === date);
        const isNull = dayData.quantity === null;
        const isEditing = editingState.key === record.key && editingState.date === date;
        const editValue = editedValues[`${record.key}-${date}`];
        const isRowClosed = record.project_stage === "Close";

        return (
          <EditableCell
            record={record}
            date={date}
            dayData={dayData}
            isEditing={isEditing}
            isNull={isNull}
            editValue={editValue}
            onEdit={handleEdit}
            onLogs={handleLogs}
            onChange={(e) => handleChangeInput(e, record, date)}
            onKeyDown={(e) => handleKeyDown(e, record, date)}
            onBlur={() => handleBlur(record, date)}
            inputRef={isEditing ? inputRef : null}
            valueMonth={valueMonth}
            isShowBtnEdit={isShowBtnEdit}
            isRowClosed={isRowClosed}
          />
        );
      }
    }))

    return [...visibleBaseColumns, ...dateColumns];
  }, [allDates, editingState, editedValues, handleEdit, handleLogs, handleChangeInput, handleKeyDown, handleBlur, showAllColumns]);

  const tableData = useMemo(() =>
    data.map((item, index) => ({
      key: index,
      description_name: item.description_name,
      pricing_models: item.pricingmodels,
      project_stage: item.project_stage,
      project_description: item.projectcription,
      sub_project_name_1: item.subprojectname1,
      sub_project_name_2: item.subprojectname2,
      list_day: item.list_day,
    }))
    , [data]);

  return (
    <>
      <Table
        columns={tableColumns}
        dataSource={tableData}
        pagination={false}
        scroll={{
          x: "100%",
          y: "56vh",
        }}
        bordered
        className="custom-table table-striped-rows"
        // Nếu dòng có project_stage là "Close" thì sẽ bôi màu đỏ
        rowClassName={(record, index) => {
          let className = index % 2 === 0 ? "table-row-light" : "table-row-dark";
          if (record.project_stage === "Close") {
            className += " table-row-closed";
          }
          return className;
        }}
        style={{ width: "98%" }}
      />

      <ModalLogDescriptionDay
        isOpen={logsModalOpen}
        onClose={() => setLogsModalOpen(false)}
        day={valueDay}
        logsDayData={logsDayData}
      />
    </>
  );
};

export default TableDetailDescription;
